/**
 * Metronome.js
 * 
 * This component combines two essential tools for worship musicians:
 * 1. A precise metronome with adjustable BPM and time signatures
 * 2. Ambient worship pads that provide tonal foundation during practice or worship
 * 
 * Key features include:
 * - Adjustable tempo (BPM) with tap tempo functionality
 * - Multiple time signatures (2/4, 3/4, 4/4, 6/8, etc.)
 * - Visual beat indicator with accent on the first beat
 * - Three types of ambient pads: warm, drone, and lush
 * - Selectable musical keys for the pads (C, C#, D, etc.)
 * - Independent volume and pan controls for both metronome and pads
 * - Mobile-responsive design
 * 
 * The metronome uses the Web Audio API for precise timing and high-quality sound generation.
 * The pads use advanced audio synthesis techniques including oscillators, filters, reverb,
 * and echo effects to create rich, atmospheric sounds perfect for worship environments.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Grid,
  Button,
  Divider,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  VolumeUp as VolumeUpIcon,
  Stop as StopIcon,
  PlayArrow as PlayArrowIcon,
  MusicNote as MusicNoteIcon,
  Waves as WavesIcon,
  LinearScale as LinearScaleIcon,
  Layers as LayersIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const timeSignatures = [
  '2/4', '3/4', '4/4', '6/8', '9/8', '12/8'
];

const padTypes = ['warm', 'drone', 'lush'];
const musicalKeys = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const ClickAndPad = () => {
  const { t } = useTranslation();
  const [bpm, setBpm] = useState(120);
  const [timeSignature, setTimeSignature] = useState('4/4');
  const [volume, setVolume] = useState(0.75); // Master volume (legacy)
  const [metronomeVolume, setMetronomeVolume] = useState(0.75); // Separate metronome volume
  const [padVolume, setPadVolume] = useState(0.75); // Separate pad volume
  const [metronomePan, setMetronomePan] = useState('center'); // 'left', 'center', or 'right'
  const [padPan, setPadPan] = useState('center'); // 'left', 'center', or 'right'
  const [padEchoAmount, setPadEchoAmount] = useState(0.4); // Echo amount from 0 to 1
  const [isPlaying, setIsPlaying] = useState(false);
  // Removed tab state since we're showing both controls simultaneously
  
  // Metronome refs
  const intervalRef = useRef(null);
  const currentBeat = useRef(0);
  const bpmChangeInterval = useRef(null);
  const bpmChangeTimeout = useRef(null);
  const audioContext = useRef(null);
  const metronomePannerNode = useRef(null); // Persistent panner node for metronome
  const [tapTimes, setTapTimes] = useState([]);
  const TAP_TEMPO_TIMEOUT = 2000; // Reset tap tempo if no tap for 2 seconds
  
  // Worship Pad state
  const [padType, setPadType] = useState('warm');
  const [padKey, setPadKey] = useState('C');
  const [isPadPlaying, setIsPadPlaying] = useState(false);
  const padAudioContext = useRef(null);
  const padOscillators = useRef([]);
  const padGainNodes = useRef([]);

  // Effect to update pad volume when padVolume slider changes
  useEffect(() => {
    // If pad is playing, update the volume
    if (isPadPlaying && padGainNodes.current.length > 0 && padAudioContext.current) {
      // Update all gain nodes to reflect the new volume
      padGainNodes.current.forEach((gainNode, index) => {
        if (gainNode && gainNode.gain) {
          // Calculate the appropriate gain for this oscillator based on its position
          let relativeGain = 1.0;
          if (index === 0) {
            relativeGain = 1.0; // Root note: full relative gain
          } else if (index === 1) {
            relativeGain = 0.85; // Fifth: slightly lower
          } else if (index === 2) {
            relativeGain = 0.78; // Octave: balanced with root
          } else if (index === 3) {
            relativeGain = 0.64; // 5th+octave: balanced
          } else {
            relativeGain = 0.42; // 2 octaves: subtle high end
          }
          
          // Apply the new volume with the relative gain adjustment
          const now = padAudioContext.current.currentTime;
          gainNode.gain.cancelScheduledValues(now);
          gainNode.gain.setValueAtTime(gainNode.gain.value, now);
          gainNode.gain.linearRampToValueAtTime(padVolume * 0.1 * relativeGain, now + 0.1);
        }
      });
    }
  }, [padVolume, isPadPlaying]);

  useEffect(() => {
    // Initialize audio context on first user interaction
    const initAudio = () => {
      if (!audioContext.current) {
        audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (!padAudioContext.current) {
        padAudioContext.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      document.removeEventListener('click', initAudio);
    };
    document.addEventListener('click', initAudio);
    return () => {
      document.removeEventListener('click', initAudio);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Clean up pad oscillators
      stopPad();
    };
  }, []);

  const playClick = (isAccent) => {
    if (!audioContext.current) return;

    // Create main oscillator for the "click" sound
    const osc = audioContext.current.createOscillator();
    const gainNode = audioContext.current.createGain();
    
    // Create noise for more percussive attack
    const noiseLength = 0.015; // 15ms of noise
    const bufferSize = audioContext.current.sampleRate * noiseLength;
    const noiseBuffer = audioContext.current.createBuffer(1, bufferSize, audioContext.current.sampleRate);
    const noise = noiseBuffer.getChannelData(0);
    
    // Fill buffer with white noise
    for (let i = 0; i < bufferSize; i++) {
      noise[i] = Math.random() * 2 - 1;
    }

    // Create noise node
    const noiseNode = audioContext.current.createBufferSource();
    const noiseGain = audioContext.current.createGain();
    noiseNode.buffer = noiseBuffer;

    // Create filter for noise to make it more "clicky"
    const filter = audioContext.current.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = isAccent ? 2500 : 1800;
    filter.Q.value = 1.0;

    // Create or reuse the stereo panner node for metronome
    if (!metronomePannerNode.current) {
      metronomePannerNode.current = audioContext.current.createStereoPanner();
      
      // Set initial pan position based on metronomePan setting
      if (metronomePan === 'left') {
        metronomePannerNode.current.pan.value = -1.0;
      } else if (metronomePan === 'right') {
        metronomePannerNode.current.pan.value = 1.0;
      } else { // center
        metronomePannerNode.current.pan.value = 0.0;
      }
      
      // Connect the panner to the destination (only needs to be done once)
      metronomePannerNode.current.connect(audioContext.current.destination);
    }
    
    // Connect everything with panning
    osc.connect(gainNode);
    gainNode.connect(metronomePannerNode.current);
    
    noiseNode.connect(filter);
    filter.connect(noiseGain);
    // Connect noise to the same panner node to ensure consistent panning
    noiseGain.connect(metronomePannerNode.current);

    const now = audioContext.current.currentTime;
    
    // Set up oscillator
    osc.frequency.value = isAccent ? 1600 : 1200;
    gainNode.gain.value = 0;

    // Very quick attack for main oscillator
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(isAccent ? metronomeVolume : metronomeVolume * 0.7, now + 0.001);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.03);

    // Set up noise envelope
    noiseGain.gain.setValueAtTime(0, now);
    noiseGain.gain.linearRampToValueAtTime(isAccent ? metronomeVolume * 0.4 : metronomeVolume * 0.3, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.02);

    // Start and stop both sounds
    osc.start(now);
    osc.stop(now + 0.03);
    noiseNode.start(now);
    noiseNode.stop(now + 0.02);

    // Clean up nodes - but don't disconnect the shared panner node
    setTimeout(() => {
      osc.disconnect();
      gainNode.disconnect();
      noiseNode.disconnect();
      filter.disconnect();
      noiseGain.disconnect();
      // We don't disconnect metronomePannerNode.current as it's shared between clicks
    }, 100);
  };

  const startStop = () => {
    if (isPlaying) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsPlaying(false);
      currentBeat.current = 0;
      
      // We don't disconnect the panner node when stopping
      // This allows the pan setting to persist between metronome sessions
    } else {
      // Resume audio context if suspended (needed for iOS)
      if (audioContext.current?.state === 'suspended') {
        audioContext.current.resume();
      }

      currentBeat.current = 0;
      const beatsPerBar = parseInt(timeSignature.split('/')[0]);
      const interval = (60 / bpm) * 1000;

      // Play first beat immediately
      playClick(true);
      currentBeat.current = (currentBeat.current + 1) % beatsPerBar;

      intervalRef.current = setInterval(() => {
        playClick(currentBeat.current === 0);
        currentBeat.current = (currentBeat.current + 1) % beatsPerBar;
      }, interval);

      setIsPlaying(true);
    }
  };

  const handleBpmChange = (newBpm) => {
    const validBpm = Math.min(Math.max(newBpm, 40), 220);
    setBpm(validBpm);
    
    if (isPlaying) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      const beatsPerBar = parseInt(timeSignature.split('/')[0]);
      const interval = (60 / validBpm) * 1000;
      
      intervalRef.current = setInterval(() => {
        playClick(currentBeat.current === 0);
        currentBeat.current = (currentBeat.current + 1) % beatsPerBar;
      }, interval);
    }
  };

  const handleTimeSignatureChange = (event) => {
    setTimeSignature(event.target.value);
    currentBeat.current = 0;
    
    if (isPlaying) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      const beatsPerBar = parseInt(event.target.value.split('/')[0]);
      const interval = (60 / bpm) * 1000;
      
      intervalRef.current = setInterval(() => {
        playClick(currentBeat.current === 0);
        currentBeat.current = (currentBeat.current + 1) % beatsPerBar;
      }, interval);
    }
  };

  const handleTap = () => {
    const now = performance.now();
    
    setTapTimes(prevTimes => {
      const recentTaps = [...prevTimes, now].filter(time => now - time < TAP_TEMPO_TIMEOUT);
      
      if (recentTaps.length > 1) {
        const intervals = [];
        for (let i = 1; i < recentTaps.length; i++) {
          intervals.push(recentTaps[i] - recentTaps[i - 1]);
        }
        
        // Calculate average interval, excluding outliers
        const validIntervals = intervals.filter(interval => {
          const mean = intervals.reduce((a, b) => a + b) / intervals.length;
          const deviation = Math.abs(interval - mean);
          return deviation < mean * 0.5; // Filter out intervals that deviate more than 50%
        });
        
        if (validIntervals.length > 0) {
          const averageInterval = validIntervals.reduce((a, b) => a + b) / validIntervals.length;
          const newBpm = Math.round(60000 / averageInterval);
          
          if (newBpm >= 40 && newBpm <= 220) {
            handleBpmChange(newBpm);
          }
        }
      }
      
      return recentTaps;
    });
  };

  // Clear tap times after timeout
  useEffect(() => {
    const clearTapTimesTimeout = setTimeout(() => {
      setTapTimes([]);
    }, TAP_TEMPO_TIMEOUT);

    return () => clearTimeout(clearTapTimesTimeout);
  }, [tapTimes]);

  // Handle BPM change with continuous adjustment
  const startBpmChange = (increment) => {
    if (bpmChangeInterval.current) return; // Prevent multiple intervals

    const change = () => {
      setBpm(prev => {
        const newBpm = prev + increment;
        const validBpm = Math.min(Math.max(newBpm, 40), 220);
        
        if (isPlaying && validBpm !== prev) {
          // Update interval if playing
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          
          const beatsPerBar = parseInt(timeSignature.split('/')[0]);
          const interval = (60 / validBpm) * 1000;
          
          intervalRef.current = setInterval(() => {
            playClick(currentBeat.current === 0);
            currentBeat.current = (currentBeat.current + 1) % beatsPerBar;
          }, interval);
        }
        
        return validBpm;
      });
    };

    // Initial change
    change();

    // Start slow changes
    bpmChangeTimeout.current = setTimeout(() => {
      change();
      bpmChangeInterval.current = setInterval(change, 100); // Change every 100ms
    }, 500); // Start continuous change after 500ms
  };

  const stopBpmChange = () => {
    if (bpmChangeTimeout.current) {
      clearTimeout(bpmChangeTimeout.current);
      bpmChangeTimeout.current = null;
    }
    if (bpmChangeInterval.current) {
      clearInterval(bpmChangeInterval.current);
      bpmChangeInterval.current = null;
    }
  };

  // Worship Pad functions
  const getFrequencyForKey = (key, octave = 3) => {
    const baseFrequencies = {
      'C': 261.63, 'C#': 277.18, 'D': 293.66, 'D#': 311.13,
      'E': 329.63, 'F': 349.23, 'F#': 369.99, 'G': 392.00,
      'G#': 415.30, 'A': 440.00, 'A#': 466.16, 'B': 493.88
    };
    
    const baseFreq = baseFrequencies[key];
    const octaveOffset = octave - 4; // Standard octave is 4 for these frequencies
    return baseFreq * Math.pow(2, octaveOffset);
  };

  // Helper function to create pad sound with a specific key
  const createPadSoundWithKey = (specificKey) => {
    if (!padAudioContext.current) {
      padAudioContext.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    // Resume audio context if suspended (needed for iOS)
    if (padAudioContext.current.state === 'suspended') {
      padAudioContext.current.resume();
    }
    
    // Make sure any existing oscillators are cleaned up
    // but we don't call stopPad here since our handlers already do that
    
    // Create reverb for the pad
    const reverbNode = padAudioContext.current.createConvolver();
    
    // Adjust reverb time based on pad type
    const reverbTime = 3.0; // seconds
    const sampleRate = padAudioContext.current.sampleRate;
    const reverbLength = sampleRate * reverbTime;
    const reverbBuffer = padAudioContext.current.createBuffer(2, reverbLength, sampleRate);
    
    // Create reverb impulse response
    for (let channel = 0; channel < 2; channel++) {
      const channelData = reverbBuffer.getChannelData(channel);
      for (let i = 0; i < reverbLength; i++) {
        channelData[i] = (Math.random() * 2 - 1) * Math.exp(-i / (sampleRate * 1.5));
      }
    }
    reverbNode.buffer = reverbBuffer;
    

    
    // Connect reverb to main output
    const panNode = padAudioContext.current.createStereoPanner();
    // Convert string pan values to numeric values for the Web Audio API
    let panValue = 0; // Default to center (0)
    if (padPan === 'left') panValue = -1;
    else if (padPan === 'right') panValue = 1;
    panNode.pan.value = panValue;
    
    // Connect the audio chain
    // oscillators -> masterGain -> reverbNode -> panNode -> destination
    reverbNode.connect(panNode);
    panNode.connect(padAudioContext.current.destination);
    
    // Create master gain node for overall pad volume
    const masterGain = padAudioContext.current.createGain();
    masterGain.gain.value = padVolume; // padVolume is already in 0-1 range (0.75)
    masterGain.connect(reverbNode);
    
    // Store oscillators and gain nodes for later access
    const oscillators = [];
    const gainNodes = [];
    
    // Create different oscillator configurations based on pad type
    if (padType === 'warm') {
      // Warm pad: Root, fifth, octave, fifth+octave, 2 octaves
      // Use the key that was passed in directly
      const rootFreq = getFrequencyForKey(specificKey, 3); // Lower octave for warm pad
      const intervals = [1, 1.5, 2, 3, 4]; // Harmonic series intervals
      
      intervals.forEach((interval, index) => {
        // Create oscillator
        const osc = padAudioContext.current.createOscillator();
        osc.type = index === 0 ? 'sine' : 'triangle'; // Root is sine, harmonics are triangle
        osc.frequency.value = rootFreq * interval;
        
        // Add slight detuning for richness
        if (index > 0) {
          osc.detune.value = Math.random() * 10 - 5; // +/- 5 cents
        }
        
        // Create gain node for this oscillator
        const gainNode = padAudioContext.current.createGain();
        gainNode.gain.value = 0; // Start silent
        
        // Connect oscillator to its gain node, then to master gain
        osc.connect(gainNode);
        gainNode.connect(masterGain);
        
        // Fade in the oscillator
        const now = padAudioContext.current.currentTime;
        const attackTime = 2.0; // 2 second fade-in
        
        // Different gain levels for different harmonics
        let maxGain = 0;
        if (index === 0) {
          maxGain = 0.12; // Root note: prominent
        } else if (index === 1) {
          maxGain = 0.08; // Fifth: strong
        } else if (index === 2) {
          maxGain = 0.07; // Octave: balanced with root
        } else if (index === 3) {
          maxGain = 0.045; // 5th+octave: balanced with other harmonics
        } else {
          maxGain = 0.03; // 2 octaves: subtle high end
        }
        
        gainNode.gain.linearRampToValueAtTime(maxGain, now + attackTime);
        
        // Start oscillator
        osc.start();
        
        // Store references
        oscillators.push(osc);
        gainNodes.push(gainNode);
      });
    } else if (padType === 'drone') {
      // Drone pad: Multiple oscillators with slow LFO modulation
      // Use the key that was passed in directly
      const rootFreq = getFrequencyForKey(specificKey, 3);
      const intervals = [1, 2, 1.5, 0.5]; // Root, octave, fifth, and sub-octave
      
      intervals.forEach((interval, index) => {
        // Create main oscillator
        const osc = padAudioContext.current.createOscillator();
        osc.type = index % 2 === 0 ? 'sawtooth' : 'square'; // Alternate waveforms
        osc.frequency.value = rootFreq * interval;
        
        // Add heavy detuning for drone effect
        osc.detune.value = Math.random() * 14 - 7; // +/- 7 cents
        
        // Create gain node for this oscillator
        const gainNode = padAudioContext.current.createGain();
        gainNode.gain.value = 0; // Start silent
        
        // Connect oscillator to its gain node, then to master gain
        osc.connect(gainNode);
        gainNode.connect(masterGain);
        
        // Fade in the oscillator with a longer attack for drone
        const now = padAudioContext.current.currentTime;
        const attackTime = 3.0; // 3 second fade-in for drone
        
        // Different gain levels for different harmonics
        let maxGain = 0;
        if (index === 0) {
          maxGain = 0.1; // Root note
        } else if (index === 1) {
          maxGain = 0.08; // Octave
        } else if (index === 2) {
          maxGain = 0.06; // Fifth
        } else {
          maxGain = 0.05; // Sub-octave
        }
        
        gainNode.gain.linearRampToValueAtTime(maxGain, now + attackTime);
        
        // Start oscillator
        osc.start();
        
        // Store references
        oscillators.push(osc);
        gainNodes.push(gainNode);
      });
    } else if (padType === 'lush') {
      // Lush pad: Contemporary worship pad with rich reverb and warm layers
      // Use the key that was passed in directly
      const rootFreq = getFrequencyForKey(specificKey, 3); // Middle octave for foundation
      
      // Create multiple oscillators for a rich, layered sound
      const oscillatorConfigs = [
        { interval: 1, type: 'sine', octave: 0, detune: 0, gain: 0.09 },      // Root note, pure
        { interval: 1, type: 'triangle', octave: 0, detune: 3, gain: 0.07 },   // Root note, slightly detuned
        { interval: 2, type: 'sine', octave: 0, detune: -2, gain: 0.06 },      // Octave above
        { interval: 1.5, type: 'sine', octave: 0, detune: 2, gain: 0.05 },     // Fifth
        { interval: 0.5, type: 'sine', octave: 0, detune: -4, gain: 0.05 },     // Octave below
        { interval: 1, type: 'sine', octave: 1, detune: 5, gain: 0.04 },       // Two octaves above
        { interval: 3, type: 'sine', octave: 0, detune: -3, gain: 0.035 },     // Octave + fifth
        { interval: 0.75, type: 'triangle', octave: 0, detune: 4, gain: 0.03 }  // Fifth below
      ];
      
      oscillatorConfigs.forEach(config => {
        // Create oscillator
        const osc = padAudioContext.current.createOscillator();
        osc.type = config.type;
        
        // Calculate frequency based on interval and octave
        const octaveMultiplier = Math.pow(2, config.octave);
        osc.frequency.value = rootFreq * config.interval * octaveMultiplier;
        
        // Add subtle detuning for richness
        osc.detune.value = config.detune;
        
        // Create gain node for this oscillator
        const gainNode = padAudioContext.current.createGain();
        gainNode.gain.value = 0; // Start silent
        
        // Connect oscillator to its gain node, then to master gain
        osc.connect(gainNode);
        gainNode.connect(masterGain);
        
        // Fade in the oscillator with a very slow attack for lush pad
        const now = padAudioContext.current.currentTime;
        const attackTime = 3.5 + (Math.random() * 1.5); // 3.5-5 second fade-in for smooth entrance
        
        // Set gain level based on config
        gainNode.gain.linearRampToValueAtTime(config.gain, now + attackTime);
        
        // Start oscillator
        osc.start();
        
        // Store references
        oscillators.push(osc);
        gainNodes.push(gainNode);
      });
      
      // Add a filter to soften the sound and create impression of distance
      const filter = padAudioContext.current.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 2000; // Cut higher frequencies
      filter.Q.value = 0.5; // Gentle slope
      
      // Insert filter before reverb for a more distant sound
      masterGain.disconnect(reverbNode);
      masterGain.connect(filter);
      filter.connect(reverbNode);
    }
    
    // Save references
    padOscillators.current = oscillators;
    padGainNodes.current = gainNodes;
    
    setIsPadPlaying(true);
  };
  
  // Helper function to create pad sound with a specific key and explicit pad type
  // This ensures we're using the correct pad type even if the state hasn't updated yet
  const createPadSoundWithExplicitType = (specificKey, explicitPadType) => {
    if (!padAudioContext.current) {
      padAudioContext.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    // Resume audio context if suspended (needed for iOS)
    if (padAudioContext.current.state === 'suspended') {
      padAudioContext.current.resume();
    }
    
    // Make sure any existing oscillators are cleaned up
    // but we don't call stopPad here since our handlers already do that
    
    // Create reverb for the pad
    const reverbNode = padAudioContext.current.createConvolver();
    
    // Adjust reverb time based on pad type - longer for lush
    const reverbTime = explicitPadType === 'lush' ? 5.0 : 3.0; // seconds - longer for lush
    const sampleRate = padAudioContext.current.sampleRate;
    const reverbLength = sampleRate * reverbTime;
    const reverbBuffer = padAudioContext.current.createBuffer(2, reverbLength, sampleRate);
    
    // Create reverb impulse response - more diffuse for lush
    for (let channel = 0; channel < 2; channel++) {
      const channelData = reverbBuffer.getChannelData(channel);
      for (let i = 0; i < reverbLength; i++) {
        // For lush, create a more diffuse, longer decay with a slower initial decay
        if (explicitPadType === 'lush') {
          // Use a slower decay curve for more spacious reverb
          channelData[i] = (Math.random() * 2 - 1) * Math.exp(-i / (sampleRate * 1.2));
        } else {
          channelData[i] = (Math.random() * 2 - 1) * Math.exp(-i / (sampleRate * 1.5));
        }
      }
    }
    reverbNode.buffer = reverbBuffer;
    

    
    // Connect reverb to main output
    const panNode = padAudioContext.current.createStereoPanner();
    // Convert string pan values to numeric values for the Web Audio API
    let panValue = 0; // Default to center (0)
    if (padPan === 'left') panValue = -1;
    else if (padPan === 'right') panValue = 1;
    panNode.pan.value = panValue;
    
    // Connect the audio chain
    // For all pad types: oscillators -> masterGain -> reverbNode -> panNode -> destination
    reverbNode.connect(panNode);
    panNode.connect(padAudioContext.current.destination);
    
    // Create master gain node for overall pad volume
    const masterGain = padAudioContext.current.createGain();
    masterGain.gain.value = padVolume; // padVolume is already in 0-1 range (0.75)
    masterGain.connect(reverbNode);
    
    // Store oscillators and gain nodes for later access
    const oscillators = [];
    const gainNodes = [];
    
    // Create different oscillator configurations based on pad type
    if (explicitPadType === 'warm') {
      // Warm pad: Root, fifth, octave, fifth+octave, 2 octaves
      // Use the key that was passed in directly
      const rootFreq = getFrequencyForKey(specificKey, 3); // Lower octave for warm pad
      const intervals = [1, 1.5, 2, 3, 4]; // Harmonic series intervals
      
      intervals.forEach((interval, index) => {
        // Create oscillator
        const osc = padAudioContext.current.createOscillator();
        osc.type = index === 0 ? 'sine' : 'triangle'; // Root is sine, harmonics are triangle
        osc.frequency.value = rootFreq * interval;
        
        // Add slight detuning for richness
        if (index > 0) {
          osc.detune.value = Math.random() * 10 - 5; // +/- 5 cents
        }
        
        // Create gain node for this oscillator
        const gainNode = padAudioContext.current.createGain();
        gainNode.gain.value = 0; // Start silent
        
        // Connect oscillator to its gain node, then to master gain
        osc.connect(gainNode);
        gainNode.connect(masterGain);
        
        // Fade in the oscillator
        const now = padAudioContext.current.currentTime;
        const attackTime = 2.0; // 2 second fade-in
        
        // Different gain levels for different harmonics
        let maxGain = 0;
        if (index === 0) {
          maxGain = 0.12; // Root note: prominent
        } else if (index === 1) {
          maxGain = 0.08; // Fifth: strong
        } else if (index === 2) {
          maxGain = 0.07; // Octave: balanced with root
        } else if (index === 3) {
          maxGain = 0.045; // 5th+octave: balanced with other harmonics
        } else {
          maxGain = 0.03; // 2 octaves: subtle high end
        }
        
        gainNode.gain.linearRampToValueAtTime(maxGain, now + attackTime);
        
        // Start oscillator
        osc.start();
        
        // Store references
        oscillators.push(osc);
        gainNodes.push(gainNode);
      });
    } else if (explicitPadType === 'drone') {
      // Drone pad: Multiple oscillators with slow LFO modulation
      // Use the key that was passed in directly
      const rootFreq = getFrequencyForKey(specificKey, 3);
      const intervals = [1, 2, 1.5, 0.5]; // Root, octave, fifth, and sub-octave
      
      intervals.forEach((interval, index) => {
        // Create main oscillator
        const osc = padAudioContext.current.createOscillator();
        osc.type = index % 2 === 0 ? 'sawtooth' : 'square'; // Alternate waveforms
        osc.frequency.value = rootFreq * interval;
        
        // Add heavy detuning for drone effect
        osc.detune.value = Math.random() * 14 - 7; // +/- 7 cents
        
        // Create gain node for this oscillator
        const gainNode = padAudioContext.current.createGain();
        gainNode.gain.value = 0; // Start silent
        
        // Connect oscillator to its gain node, then to master gain
        osc.connect(gainNode);
        gainNode.connect(masterGain);
        
        // Fade in the oscillator with a longer attack for drone
        const now = padAudioContext.current.currentTime;
        const attackTime = 3.0; // 3 second fade-in for drone
        
        // Different gain levels for different harmonics
        let maxGain = 0;
        if (index === 0) {
          maxGain = 0.1; // Root note
        } else if (index === 1) {
          maxGain = 0.08; // Octave
        } else if (index === 2) {
          maxGain = 0.06; // Fifth
        } else {
          maxGain = 0.05; // Sub-octave
        }
        
        gainNode.gain.linearRampToValueAtTime(maxGain, now + attackTime);
        
        // Start oscillator
        osc.start();
        
        // Store references
        oscillators.push(osc);
        gainNodes.push(gainNode);
      });
    } else if (explicitPadType === 'lush') {
      // Lush pad: Contemporary worship pad with rich reverb and warm layers
      // Use the key that was passed in directly
      const rootFreq = getFrequencyForKey(specificKey, 3); // Middle octave for foundation
      
      // Create multiple oscillators for a rich, layered sound
      const oscillatorConfigs = [
        { interval: 1, type: 'sine', octave: 0, detune: 0, gain: 0.09 },      // Root note, pure
        { interval: 1, type: 'triangle', octave: 0, detune: 3, gain: 0.07 },   // Root note, slightly detuned
        { interval: 2, type: 'sine', octave: 0, detune: -2, gain: 0.06 },      // Octave above
        { interval: 1.5, type: 'sine', octave: 0, detune: 2, gain: 0.05 },     // Fifth
        { interval: 0.5, type: 'sine', octave: 0, detune: -4, gain: 0.05 },     // Octave below
        { interval: 1, type: 'sine', octave: 1, detune: 5, gain: 0.04 },       // Two octaves above
        { interval: 3, type: 'sine', octave: 0, detune: -3, gain: 0.035 },     // Octave + fifth
        { interval: 0.75, type: 'triangle', octave: 0, detune: 4, gain: 0.03 }  // Fifth below
      ];
      
      oscillatorConfigs.forEach(config => {
        // Create oscillator
        const osc = padAudioContext.current.createOscillator();
        osc.type = config.type;
        
        // Calculate frequency based on interval and octave
        const octaveMultiplier = Math.pow(2, config.octave);
        osc.frequency.value = rootFreq * config.interval * octaveMultiplier;
        
        // Add subtle detuning for richness
        osc.detune.value = config.detune;
        
        // Create gain node for this oscillator
        const gainNode = padAudioContext.current.createGain();
        gainNode.gain.value = 0; // Start silent
        
        // Connect oscillator to its gain node, then to master gain
        osc.connect(gainNode);
        gainNode.connect(masterGain);
        
        // Fade in the oscillator with a very slow attack for lush pad
        const now = padAudioContext.current.currentTime;
        const attackTime = 3.5 + (Math.random() * 1.5); // 3.5-5 second fade-in for smooth entrance
        
        // Set gain level based on config
        gainNode.gain.linearRampToValueAtTime(config.gain, now + attackTime);
        
        // Start oscillator
        osc.start();
        
        // Store references
        oscillators.push(osc);
        gainNodes.push(gainNode);
      });
      
      // Add a filter to soften the sound and create impression of distance
      const filter = padAudioContext.current.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 2000; // Cut higher frequencies
      filter.Q.value = 0.5; // Gentle slope
      
      // Insert filter before reverb for a more distant sound
      masterGain.disconnect(reverbNode);
      masterGain.connect(filter);
      filter.connect(reverbNode);
    }
    
    // Save references
    padOscillators.current = oscillators;
    padGainNodes.current = gainNodes;
    
    setIsPadPlaying(true);
  };

  const createPadSound = () => {
    // Simply call our helper function with the current key
    createPadSoundWithKey(padKey);
    setIsPadPlaying(true);
  };

  const stopPad = (updateState = true) => {
    if (padOscillators.current.length > 0) {
      const now = padAudioContext.current?.currentTime || 0;
      
      // Fade out all oscillators - longer, more graceful fadeout for lush pad
      const fadeOutTime = padType === 'lush' ? 3.0 : 1.5; // Longer fadeout for lush
      
      padGainNodes.current.forEach((gainNode, index) => {
        if (gainNode && gainNode.gain) {
          gainNode.gain.cancelScheduledValues(now);
          gainNode.gain.setValueAtTime(gainNode.gain.value, now);
          
          // For lush pad, create staggered fadeouts for a more ethereal ending
          if (padType === 'lush') {
            // Stagger the fadeout times slightly for each oscillator
            const staggeredTime = fadeOutTime - (index * 0.1);
            // Use exponential ramp for smoother fadeout (more natural sounding)
            // Set a minimum value to avoid the exponential ramp reaching zero (which is invalid)
            const minValue = 0.0001;
            gainNode.gain.setTargetAtTime(minValue, now, staggeredTime / 3);
          } else {
            // Standard linear fadeout for other pad types
            gainNode.gain.linearRampToValueAtTime(0, now + fadeOutTime);
          }
        }
      });
      
      // Stop oscillators after fade out - longer timeout for lush
      const timeoutDuration = padType === 'lush' ? 3500 : 1800; // ms
      setTimeout(() => {
        padOscillators.current.forEach(osc => {
          if (osc) {
            try {
              osc.stop();
              osc.disconnect();
            } catch (e) {
              // Ignore errors if oscillator was already stopped
            }
          }
        });
        padGainNodes.current.forEach(gainNode => {
          if (gainNode) {
            try {
              gainNode.disconnect();
            } catch (e) {
              // Ignore errors if already disconnected
            }
          }
        });
        padOscillators.current = [];
        padGainNodes.current = [];
        
        // Clear the pad panner reference
        currentPadPanner.current = null;
      }, timeoutDuration);
    }
    
    if (updateState) {
      setIsPadPlaying(false);
    }
  };

  const handlePadTypeChange = (event) => {
    // Get the new type value
    const newType = event.target.value;
    
    // Check if pad is playing
    if (isPadPlaying && padAudioContext.current) {
      // First, fade out the current pad sound
      const now = padAudioContext.current.currentTime;
      const fadeOutTime = 1.0; // 1 second fade-out
      
      // Fade out all oscillators
      padGainNodes.current.forEach((gainNode) => {
        if (gainNode && gainNode.gain) {
          gainNode.gain.cancelScheduledValues(now);
          gainNode.gain.setValueAtTime(gainNode.gain.value, now);
          gainNode.gain.linearRampToValueAtTime(0, now + fadeOutTime);
        }
      });
      
      // Wait for fade out, then close the audio context and create a new one with the new type
      setTimeout(() => {
        if (padAudioContext.current) {
          padAudioContext.current.close().then(() => {
            padAudioContext.current = null;
            padOscillators.current = [];
            padGainNodes.current = [];
            
            // Update the type state
            setPadType(newType);
            
            // Create a new audio context and start the pad with the new type
            setTimeout(() => {
              createPadSoundWithExplicitType(padKey, newType);
            }, 100);
          }).catch(err => {
            console.error('Error closing audio context:', err);
            // Fallback if closing fails
            padAudioContext.current = null;
            padOscillators.current = [];
            padGainNodes.current = [];
            
            // Update the type state
            setPadType(newType);
            
            setTimeout(() => {
              createPadSoundWithExplicitType(padKey, newType);
            }, 100);
          });
        }
      }, fadeOutTime * 1000);
    } else {
      // If no audio context exists or pad is not playing, just update the type
      setPadType(newType);
      
      // If pad is playing, create a new sound with the new type
      if (isPadPlaying) {
        setTimeout(() => {
          createPadSoundWithExplicitType(padKey, newType);
        }, 100);
      }
    }
  };

  const handlePadKeyChange = (event) => {
    // Get the new key value
    const newKey = event.target.value;
    
    // Check if pad is playing
    if (isPadPlaying && padAudioContext.current) {
      // First, fade out the current pad sound
      const now = padAudioContext.current.currentTime;
      const fadeOutTime = 1.0; // 1 second fade-out
      
      // Fade out all oscillators
      padGainNodes.current.forEach((gainNode) => {
        if (gainNode && gainNode.gain) {
          gainNode.gain.cancelScheduledValues(now);
          gainNode.gain.setValueAtTime(gainNode.gain.value, now);
          gainNode.gain.linearRampToValueAtTime(0, now + fadeOutTime);
        }
      });
      
      // Wait for fade out, then close the audio context and create a new one with the new key
      setTimeout(() => {
        if (padAudioContext.current) {
          padAudioContext.current.close().then(() => {
            padAudioContext.current = null;
            padOscillators.current = [];
            padGainNodes.current = [];
            
            // Update the key state
            setPadKey(newKey);
            
            // Create a new audio context and start the pad
            setTimeout(() => {
              createPadSoundWithKey(newKey);
            }, 100);
          }).catch(err => {
            console.error('Error closing audio context:', err);
            // Fallback if closing fails
            padAudioContext.current = null;
            padOscillators.current = [];
            padGainNodes.current = [];
            
            // Update the key state
            setPadKey(newKey);
            
            setTimeout(() => {
              createPadSoundWithKey(newKey);
            }, 100);
          });
        }
      }, fadeOutTime * 1000);
    } else {
      // If no audio context exists or pad is not playing, just update the key
      setPadKey(newKey);
      
      // If pad is not playing, we don't need to create a new sound
      if (isPadPlaying) {
        setTimeout(() => {
          createPadSoundWithKey(newKey);
        }, 100);
      }
    }
  };

  const togglePad = () => {
    if (isPadPlaying) {
      stopPad();
    } else {
      createPadSound();
    }
    // Note: We don't stop the metronome when toggling the pad
    // This allows both to play simultaneously
  };

  // Removed tab change handler since we're showing both controls simultaneously

  // Handle metronome volume change
  const handleMetronomeVolumeChange = (event, newValue) => {
    setMetronomeVolume(newValue);
    // Volume is applied in the playClick function when metronome clicks
  };
  
  // Handle pad volume change
  const handlePadVolumeChange = (event, newValue) => {
    setPadVolume(newValue);
    // Volume is applied through the useEffect that watches padVolume
  };
  
  // Reference to store the current pad panner node
  const currentPadPanner = useRef(null);

  // Handle metronome pan change
  const handleMetronomePanChange = (event, newValue) => {
    if (!newValue) return; // Ensure we have a valid value
    setMetronomePan(newValue);
    
    // Update the shared metronome panner node if it exists
    if (metronomePannerNode.current) {
      const panValue = newValue === 'left' ? -1.0 : (newValue === 'right' ? 1.0 : 0.0);
      metronomePannerNode.current.pan.value = panValue;
    }
    // Future clicks will use the new pan setting via playClick function
  };
  
  // Handle pad pan change
  const handlePadPanChange = (event, newValue) => {
    if (!newValue) return; // Ensure we have a valid value
    setPadPan(newValue);
    
    // If we have a reference to the current pad panner, update it directly
    if (currentPadPanner.current) {
      const panValue = newValue === 'left' ? -1.0 : (newValue === 'right' ? 1.0 : 0.0);
      currentPadPanner.current.pan.value = panValue;
    }
    // Otherwise, if pad is playing, we need to restart it
    else if (isPadPlaying && padAudioContext.current) {
      // Since we can't easily access the existing panner node,
      // we'll restart the pad to apply the new pan setting
      const currentKey = padKey;
      const currentType = padType;
      stopPad();
      // Short timeout to ensure the pad has stopped before restarting
      setTimeout(() => {
        setPadKey(currentKey);
        setPadType(currentType);
        createPadSound();
      }, 100);
    }
  };
  
  // Reference to store the current feedback gain node for echo effect
  const feedbackGain = useRef(null);
  
  // Handle pad echo amount change
  const handlePadEchoChange = (event, newValue) => {
    setPadEchoAmount(newValue);
    
    // If the pad is currently playing and it's a clouds pad, update the echo in real-time
    if (isPadPlaying && padType === 'clouds' && feedbackGain.current) {
      feedbackGain.current.gain.value = newValue;
    }
  };
  
  // Legacy volume handler for backward compatibility
  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
    setPadVolume(newValue);
    setMetronomeVolume(newValue);
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        p: { xs: 2, sm: 3 },
        maxWidth: '100%',
        margin: '0 auto'
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 4 },
          borderRadius: 4,
          width: '100%',
          maxWidth: 600,
          textAlign: 'center',
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'primary.dark' : 'white',
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          {t('metronome.title')}
        </Typography>
        
        {/* Metronome Controls */}
        <Grid container spacing={3} alignItems="center" justifyContent="center">
          {/* Play/Stop Button */}
          <Grid item xs={3} sm={2} md={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <IconButton
              onClick={startStop}
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: isPlaying ? 'error.main' : 'primary.main',
                color: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: isPlaying ? 'error.dark' : 'primary.dark',
                  transform: 'scale(1.05)',
                },
              }}
            >
              {isPlaying ? (
                <StopIcon sx={{ fontSize: 24 }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 24 }} />
              )}
            </IconButton>
          </Grid>
            
          {/* Time Signature */}
          <Grid item xs={9} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('metronome.timeSignature')}</InputLabel>
              <Select
                value={timeSignature}
                onChange={handleTimeSignatureChange}
                label={t('metronome.timeSignature')}
              >
                {timeSignatures.map(signature => (
                  <MenuItem key={signature} value={signature}>{signature}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* BPM Display and Controls with Tap Tempo */}
          <Grid item xs={12} sm={6} md={8}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'nowrap'
            }}>

              {/* BPM Controls */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1
                }}>
                  <IconButton
                    onMouseDown={() => startBpmChange(-1)}
                    onMouseUp={stopBpmChange}
                    onMouseLeave={stopBpmChange}
                    onTouchStart={() => startBpmChange(-1)}
                    onTouchEnd={stopBpmChange}
                    size="small"
                    sx={{ 
                      color: 'text.primary',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                  
                  <Typography variant="h4" component="div" sx={{ 
                    minWidth: { xs: 60, sm: 80 },
                    textAlign: 'center',
                    userSelect: 'none',
                    fontSize: { xs: '1.75rem', sm: '2.125rem' }
                  }}>
                    {bpm}
                  </Typography>

                  <IconButton
                    onMouseDown={() => startBpmChange(1)}
                    onMouseUp={stopBpmChange}
                    onMouseLeave={stopBpmChange}
                    onTouchStart={() => startBpmChange(1)}
                    onTouchEnd={stopBpmChange}
                    size="small"
                    sx={{ 
                      color: 'text.primary',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('metronome.bpm')}
                </Typography>
              </Box>
              
              {/* Tap Tempo - Now in the same row as BPM */}
              <Button
                variant="contained"
                color="secondary"
                onClick={handleTap}
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 1,
                  py: 0.75,
                  ml: 1,
                  borderRadius: 4,
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 3,
                    transform: 'scale(1.02)'
                  }
                }}
              >
                {t('metronome.tapTempo')}
              </Button>
            </Box>
          </Grid>
          
          {/* Metronome Volume and Pan Controls */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row' },
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                mt: 2,
                gap: { xs: 2, sm: 4 }
              }}
            >
              {/* Volume Control */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: { xs: '50%', sm: '40%' }
                }}
              >
                <Typography id="metronome-volume-slider" variant="body2" gutterBottom>
                  {t('metronome.metronomeVolume')}
                </Typography>
                <Slider
                  value={metronomeVolume}
                  onChange={handleMetronomeVolumeChange}
                  aria-labelledby="metronome-volume-slider"
                  min={0}
                  max={1}
                  step={0.01}
                  sx={{ width: '100%' }}
                />
              </Box>
              
              {/* Pan Control */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
              >
                <Typography variant="body2" gutterBottom>
                  {t('metronome.metronomePan')}
                </Typography>
                <ToggleButtonGroup
                  value={metronomePan}
                  exclusive
                  onChange={(e, newValue) => handleMetronomePanChange(e, newValue)}
                  aria-label="metronome pan"
                  color="primary"
                  size="small"
                >
                  <ToggleButton value="left" aria-label="left" sx={{ px: { xs: 2, sm: 3 } }}>
                    L
                  </ToggleButton>
                  <ToggleButton value="center" aria-label="center" sx={{ px: { xs: 2, sm: 3 } }}>
                    C
                  </ToggleButton>
                  <ToggleButton value="right" aria-label="right" sx={{ px: { xs: 2, sm: 3 } }}>
                    R
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          {t('metronome.padSection')}
        </Typography>
        
        {/* Worship Pad Controls */}
        <Grid container spacing={3} alignItems="center" justifyContent="center">
            {/* Pad Type Selection with Visual Thumbnails */}
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ mb: 1, textAlign: 'center' }}>
                {t('metronome.padType')}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                gap: 2,
                flexWrap: 'wrap',
                mb: 2
              }}>
                {padTypes.map(type => (
                  <Paper
                    key={type}
                    elevation={padType === type ? 6 : 1}
                    onClick={() => handlePadTypeChange({ target: { value: type } })}
                    sx={{
                      width: 100,
                      height: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: padType === type ? 'primary.light' : 'background.paper',
                      color: padType === type ? 'primary.contrastText' : 'text.primary',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        backgroundColor: padType === type ? 'primary.light' : 'action.hover'
                      },
                      borderRadius: 2,
                      p: 1
                    }}
                  >
                    {/* Icons for each pad type */}
                    {type === 'warm' && <WavesIcon sx={{ fontSize: 40, mb: 1 }} />}
                    {type === 'drone' && <LinearScaleIcon sx={{ fontSize: 40, mb: 1 }} />}
                    {type === 'lush' && <LayersIcon sx={{ fontSize: 40, mb: 1 }} />}
                    <Typography variant="body2">
                      {t(`metronome.padTypes.${type}`)}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Grid>
            
            {/* Key Selection with Buttons */}
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ mb: 1, textAlign: 'center' }}>
                {t('metronome.padKey')}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: 1,
                mb: 2
              }}>
                {musicalKeys.map(key => (
                  <Button
                    key={key}
                    variant={padKey === key ? "contained" : "outlined"}
                    color="primary"
                    onClick={() => handlePadKeyChange({ target: { value: key } })}
                    sx={{
                      minWidth: { xs: 40, sm: 50 },
                      height: { xs: 40, sm: 50 },
                      p: 0,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    {key}
                  </Button>
                ))}
              </Box>
            </Grid>
            
            {/* Pad Volume and Pan Controls */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'row' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  mt: 2,
                  gap: { xs: 2, sm: 4 }
                }}
              >
                {/* Volume Control */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: { xs: '50%', sm: '40%' }
                  }}
                >
                  <Typography id="pad-volume-slider" variant="body2" gutterBottom>
                    {t('metronome.padVolume')}
                  </Typography>
                  <Slider
                    value={padVolume}
                    onChange={handlePadVolumeChange}
                    aria-labelledby="pad-volume-slider"
                    min={0}
                    max={1}
                    step={0.01}
                    sx={{ width: '100%' }}
                  />
                </Box>
                
                {/* Pan Control */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant="body2" gutterBottom>
                    {t('metronome.padPan')}
                  </Typography>
                  <ToggleButtonGroup
                    value={padPan}
                    exclusive
                    onChange={(e, newValue) => handlePadPanChange(e, newValue)}
                    aria-label="pad pan"
                    color="primary"
                    size="small"
                  >
                    <ToggleButton value="left" aria-label="left" sx={{ px: { xs: 2, sm: 3 } }}>
                      L
                    </ToggleButton>
                    <ToggleButton value="center" aria-label="center" sx={{ px: { xs: 2, sm: 3 } }}>
                      C
                    </ToggleButton>
                    <ToggleButton value="right" aria-label="right" sx={{ px: { xs: 2, sm: 3 } }}>
                      R
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
              

            </Grid>
            
            {/* Play/Stop Button */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color={isPadPlaying ? "error" : "primary"}
                onClick={togglePad}
                startIcon={isPadPlaying ? <StopIcon /> : <PlayArrowIcon />}
                sx={{ 
                  minWidth: 200,
                  py: 1.5,
                  borderRadius: 8,
                  textTransform: 'none',
                  fontSize: '1.1rem'
                }}
              >
                {isPadPlaying ? t('metronome.stop') : t('metronome.play')}
              </Button>
            </Grid>
          </Grid>
      </Paper>
    </Box>
  );
};

export default ClickAndPad;
